import { useCallback, useEffect, useRef, type RefObject } from "react";

export type PingHold = {
  /** The in-flight press — the screen px (to measure the move-tolerance cancel), the world point to
   *  ping, and the pending fire timer. `null` when no hold is armed. */
  pingPressRef: RefObject<{ screenX: number; screenY: number; world: { x: number; y: number }; timer: ReturnType<typeof setTimeout> } | null>;
  /** Set when a ping just fired, so the trailing `click` (the browser fires one on a no-drag press)
   *  doesn't also clear the selection — a ping is not a select-clear. */
  justPingedRef: RefObject<boolean>;
  /** Set when a token was hit-tested on THIS press, so the trailing `click` doesn't also
   *  clear the selection or fall through to placement/deselect. */
  tokenPressRef: RefObject<boolean>;
  cancelPing: () => void;
  firePing: () => void;
};

/**
 * ── Map ping ──
 * A left-press held STILL for `PING_HOLD_MS` drops a ping everyone sees — over empty board OR
 * over a token (a token press bubbles to the stage; see `Token`). There is NO charging visual
 * (it read as distracting): the hold runs on a silent timer, and the received burst (your own
 * ping echoes back) is the feedback.
 * A pointer travel past the tolerance, the start of any drag, or an early release cancels it.
 */
export function usePingHold(onPing: ((at: { x: number; y: number }) => void) | undefined): PingHold {
  const pingPressRef = useRef<{
    screenX: number;
    screenY: number;
    world: { x: number; y: number };
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  // Read `onPing` through a ref so the fire callback stays stable (no stale closure, no churn).
  const onPingRef = useRef(onPing);
  onPingRef.current = onPing;
  const justPingedRef = useRef(false);
  const tokenPressRef = useRef(false);

  const cancelPing = useCallback(() => {
    if (pingPressRef.current) clearTimeout(pingPressRef.current.timer);
    pingPressRef.current = null;
  }, []);

  // The hold reached `PING_HOLD_MS` (fired by the press timer): ping at the pressed world point
  // and clear it. Stable (reads `onPing` via ref) so the press handler can schedule it directly.
  const firePing = useCallback(() => {
    const press = pingPressRef.current;
    pingPressRef.current = null;
    if (!press) return;
    justPingedRef.current = true; // swallow the trailing click's select-clear
    onPingRef.current?.(press.world);
  }, []);

  // Stop a pending ping timer if the surface unmounts mid-hold (never fire after teardown).
  useEffect(() => cancelPing, [cancelPing]);

  return { pingPressRef, justPingedRef, tokenPressRef, cancelPing, firePing };
}
